import { Helmet } from "react-helmet-async";

const SITE_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

const DEFAULT_OG = `${SITE_URL}/og-default.png`;

/**
 * Seo — unified head manager.
 *
 * Props:
 *  title, description, image, url
 *  type ('website' | 'article' | 'product')
 *  jsonLd — object OR array of objects. Rendered as multiple <script type="application/ld+json">.
 */
export default function Seo({
  title,
  description,
  image = DEFAULT_OG,
  url,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · Karijeeva` : "Karijeeva · Cold-Pressed Coconut Oil";
  // Canonical never carries query strings (?email=, ?q=, utm_*), otherwise
  // Search Console sees duplicate URLs for the same page.
  const canonical = url || (typeof window !== "undefined" ? `${SITE_URL}${window.location.pathname}` : SITE_URL);
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Karijeeva" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}

// ---------- JSON-LD factories ----------
export const orgLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Karijeeva",
  legalName: "Kadle Global Pvt Ltd",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description:
    "Karijeeva makes cold pressed coconut oil from coconuts grown on its family farms in Tiptur and on the Uttara Kannada coast of Karnataka. Every batch is tested at an independent food testing lab.",
  address: { "@type": "PostalAddress", addressRegion: "Karnataka", addressCountry: "IN" },
  hasCredential: {
    "@type": "EducationalOccupationalCredential",
    credentialCategory: "FSSAI licence",
    identifier: "11226304000242",
    recognizedBy: { "@type": "GovernmentOrganization", name: "Food Safety and Standards Authority of India" },
  },
  sameAs: [
    "https://instagram.com/karijeeva",
    "https://facebook.com/karijeeva",
  ],
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Karijeeva",
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/products?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path || ""}`,
  })),
});

const RETURN_POLICY = {
  "@type": "MerchantReturnPolicy",
  applicableCountry: "IN",
  returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
  merchantReturnDays: 7,
  returnMethod: "https://schema.org/ReturnByMail",
};

const shipping = (price) => ({
  "@type": "OfferShippingDetails",
  shippingDestination: { "@type": "DefinedRegion", addressCountry: "IN" },
  shippingRate: { "@type": "MonetaryAmount", value: price >= 998 ? 0 : 49, currency: "INR" },
  deliveryTime: {
    "@type": "ShippingDeliveryTime",
    handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 2, unitCode: "DAY" },
    transitTime: { "@type": "QuantitativeValue", minValue: 3, maxValue: 7, unitCode: "DAY" },
  },
});

/** One Offer per size so Google can show the right price for each. */
export const productLd = (product) => {
  const url = `${SITE_URL}/products/${product.slug}`;
  const images = product.gallery?.length ? product.gallery : [`${SITE_URL}/og-default.png`];
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.short_desc,
    image: images,
    url,
    brand: { "@type": "Brand", name: "Karijeeva" },
    manufacturer: { "@id": `${SITE_URL}/#organization` },
    countryOfOrigin: "IN",
    material: "Coconut oil",
    offers: (product.variants || []).map((v) => ({
      "@type": "Offer",
      sku: v.sku,
      name: `${product.name}, ${v.size}`,
      price: v.price,
      priceCurrency: "INR",
      availability: v.stock === 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      url,
      seller: { "@id": `${SITE_URL}/#organization` },
      shippingDetails: shipping(v.price),
      hasMerchantReturnPolicy: RETURN_POLICY,
    })),
    // Ratings only when real reviews exist (IS 19000:2022, Google review snippet rules).
    aggregateRating: product.review_count > 0
      ? { "@type": "AggregateRating", ratingValue: product.avg_rating, reviewCount: product.review_count }
      : undefined,
  };
};

export const articleLd = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.excerpt,
  image: post.cover_image,
  author: { "@type": "Person", name: post.author },
  datePublished: post.published_at,
  dateModified: post.published_at,
  publisher: {
    "@type": "Organization",
    name: "Karijeeva",
    logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
  },
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});
